/**
 * Base Bridge Class
 * 
 * Abstract base class for all bridge implementations.
 * Provides common functionality and enforces consistent interface.
 */

import type {
  BridgeAvailability,
  BridgeCapabilities,
  BridgeError,
  BridgeInfo,
} from './types'

export abstract class BaseBridge {
  /**
   * The name of the bridge (must match Android/iOS exactly)
   */
  abstract readonly bridgeName: string
  
  /**
   * Overall availability level of this bridge on web
   */
  abstract readonly availability: BridgeAvailability
  
  /**
   * List of methods this bridge supports
   */
  abstract readonly methods: string[]
  
  /**
   * Initialize the bridge (request permissions, setup listeners, etc.)
   * Called once when the BridgeManager is created
   */
  async initialize(): Promise<void> {
    // Default: no initialization needed
  }
  
  /**
   * Clean up resources (remove listeners, cancel pending operations, etc.)
   * Called when the WebFrame is unmounted
   */
  cleanup(): void {
    // Default: no cleanup needed
  }
  
  /**
   * Handle a method call from the SDK
   * 
   * @param method - The method name to call
   * @param args - Arguments passed from the SDK
   * @returns The result of the method call
   * @throws BridgeError if the method fails or is unavailable
   */
  abstract handleCall(method: string, args: any[]): Promise<any>
  
  /**
   * Check if a specific method is available
   */
  isMethodAvailable(method: string): boolean {
    return this.methods.includes(method)
  }
  
  /**
   * Get capabilities for this bridge
   * Override in subclass to provide detailed capability info
   */
  getCapabilities(): BridgeCapabilities {
    return this.methods.reduce((acc, method) => {
      acc[method] = { 
        available: this.availability !== 'unavailable',
        requiresPermission: false 
      }
      return acc
    }, {} as BridgeCapabilities)
  }
  
  /**
   * Get full bridge info for SDK initialization
   */
  getBridgeInfo(): BridgeInfo {
    return {
      name: this.bridgeName,
      availability: this.availability,
      capabilities: this.getCapabilities()
    }
  }
  
  /**
   * Helper to create a bridge unavailable error
   */
  protected createUnavailableError(feature: string): BridgeError {
    return {
      code: 'BRIDGE_UNAVAILABLE',
      message: `${feature} requires the Quapp Store app`,
      action: 'SHOW_INSTALL_PROMPT'
    }
  }
  
  /**
   * Helper to create a method not found error
   */
  protected createMethodNotFoundError(method: string): BridgeError {
    return {
      code: 'METHOD_NOT_FOUND',
      message: `Method '${method}' not found on bridge '${this.bridgeName}'`,
      action: 'NONE'
    }
  }
  
  /**
   * Helper to create a permission denied error
   */
  protected createPermissionDeniedError(permission: string): BridgeError {
    return {
      code: 'PERMISSION_DENIED',
      message: `Permission denied for ${permission}`,
      action: 'SHOW_PERMISSION_PROMPT'
    }
  }
  
  /**
   * Helper to create a not supported error
   */
  protected createNotSupportedError(feature: string): BridgeError {
    return {
      code: 'NOT_SUPPORTED',
      message: `${feature} is not supported in this browser`,
      action: 'NONE'
    }
  }
}

/**
 * Base class for bridges that are completely unavailable on web
 * These bridges always throw an unavailable error
 */
export abstract class UnavailableBridge extends BaseBridge {
  readonly availability: BridgeAvailability = 'unavailable'
  
  /**
   * The feature name to display in the error message
   */
  abstract readonly featureName: string
  
  async handleCall(_method: string, _args: any[]): Promise<never> {
    throw this.createUnavailableError(this.featureName)
  }
  
  getCapabilities(): BridgeCapabilities {
    return this.methods.reduce((acc, method) => {
      acc[method] = { 
        available: false,
        fallback: 'Install Quapp Store app'
      }
      return acc
    }, {} as BridgeCapabilities)
  }
}

/**
 * Base class for bridges that support streaming data (Gyroscope, Location)
 */
export abstract class StreamingBridge extends BaseBridge {
  protected subscriptions: Map<string, any> = new Map()
  
  /**
   * Start streaming data
   */
  abstract startStreaming(subscriptionId: string, options?: any): void
  
  /**
   * Stop streaming data
   */
  abstract stopStreaming(subscriptionId: string): void
  
  /**
   * Stop all streams on cleanup
   */
  cleanup(): void {
    for (const subscriptionId of this.subscriptions.keys()) {
      this.stopStreaming(subscriptionId)
    }
    this.subscriptions.clear()
  }
}

